import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { BackButton } from 'components/UI/BackButton';
import { Heading } from 'components/UI/Heading';
import { Icon, IconType } from 'components/UI/Icon';
import { Link } from 'components/UI/Link';
import { useTranslations } from 'hooks/useTranslations';
import { ForwardMeta } from 'models/diam';
import { ButtonLinkItem } from 'models/layout';
import { ButtonGrid, Container, Details, IconContainer } from './styled';

type Props = {
  heading: string;
  details: ReactNode;
  iconType?: IconType;
  forward?: ForwardMeta;
  link?: ButtonLinkItem;
  hideBackButton?: boolean;
};

const TIME_BEFORE_REDIRECT = 3000;

export const SuccessState: FC<Props> = ({
  heading,
  details,
  iconType,
  forward,
  link,
  hideBackButton
}) => {
  const t = useTranslations();
  const forwardUri = forward?.uri;
  const [secondsUntilRedirect, setSecondsUntilRedirect] = useState(
    Math.ceil(TIME_BEFORE_REDIRECT / 1000)
  );

  useEffect(() => {
    if (forward?.automatic_redirect && forwardUri) {
      const timer = setTimeout(() => {
        globalThis.location.assign(forwardUri);
      }, TIME_BEFORE_REDIRECT);

      const interval = setInterval(() => {
        setSecondsUntilRedirect((seconds) => Math.max(seconds - 1, 1));
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [forward?.automatic_redirect, forwardUri]);

  // Redirect button
  const button = useMemo(() => {
    if (forward && forwardUri) {
      return (
        <Link to={forwardUri} target="_blank" rel="noopener noreferrer">
          {forward.text ?? forwardUri}
        </Link>
      );
    }
    if (link) {
      return <Link to={link.url}>{link.text}</Link>;
    }
    return null;
  }, [forward, forwardUri, link]);

  return (
    <Container>
      {!hideBackButton && <BackButton />}
      {iconType && (
        <IconContainer>
          <Icon type={iconType} color="brand" size="extraLarge" />
        </IconContainer>
      )}
      <Heading>{heading}</Heading>
      <Details>
        {details}
        <br />
        {forward?.automatic_redirect && forwardUri && (
          <span>
            {t('activate_account.redirecting_to', {
              text: forward.text ?? forwardUri
            })}{' '}
            {t('general.redirecting_in_seconds', {
              seconds: secondsUntilRedirect
            })}
          </span>
        )}
      </Details>
      <ButtonGrid>{button}</ButtonGrid>
    </Container>
  );
};
